import { memo, useState } from "react";
import AddAssetPanel from "./AddAssetPanel";
import { AssetReadRow, AssetEditRow } from "./AssetRows";
import { deleteAsset, renameAsset } from "./kpiAssetApi";

/**
 * KPI row with expandable asset editor.
 *
 * Assets render straight from props — the page refreshes them in the
 * background after every mutation, so there is no local copy to keep in sync.
 * Rename/delete here touch this KPI's mapping only; the cross-KPI flows live
 * in the By Asset view.
 */
function KpiAssetRow({
  kpi,
  assets,
  sources,
  selectedFYId,
  onSaved,
  disabled,
  isLast,
  canCreate,
  canUpdate,
  canDelete,
}) {
  const [open, setOpen] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const toggleOpen = () => setOpen(o => !o);

  /**
   * Only toggles when the header itself is the target — key presses on the
   * checkbox / Add button inside it must not collapse the row.
   */
  const onHeaderKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleOpen();
    }
  };

  /**
   * Delete fires immediately against the API; the row spinner stays up until
   * the background refresh lands so the row doesn't flash back untouched.
   */
  const handleDeleteSingle = async (id) => {
    setDeletingId(id);
    try {
      const res = await deleteAsset(id);
      if (res?.isSuccess !== false) await onSaved();
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditSave = async (assetId, newTitle) => {
    try {
      const res = await renameAsset(assetId, newTitle);
      if (res?.isSuccess !== false) {
        setEditingAssetId(null);
        await onSaved();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const assetCount = assets.filter(a => a.isLocationRequired).length;
  const globalCount = assets.filter(a => !a.isLocationRequired).length;

  return (
    <div style={{ borderBottom: isLast ? "none" : "1px solid var(--kpa-slate-100)" }}>
      <div
        className={`kpa-kpi-row-header${open ? " kpa-kpi-row-header--open" : ""}`}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggleOpen}
        onKeyDown={onHeaderKeyDown}
      >
        <div className={`kpa-caret${open ? " kpa-caret--open" : ""}`} aria-hidden="true">
          <span>▶</span>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="kpa-kpi-title" title={kpi.title}>{kpi.title || "—"}</div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {assetCount > 0 && (
            <span className="kpa-count-pill kpa-count-pill--blue">
              <i className="fas fa-map-marker-alt" style={{ marginRight: 4, fontSize: 9 }} aria-hidden="true" />
              {assetCount} asset{assetCount !== 1 ? "s" : ""}
            </span>
          )}
          {globalCount > 0 && (
            <span className="kpa-count-pill kpa-count-pill--amber">
              <i className="fas fa-globe" style={{ marginRight: 4, fontSize: 9 }} aria-hidden="true" />
              {globalCount} global asset{globalCount !== 1 ? "s" : ""}
            </span>
          )}
          {assetCount === 0 && globalCount === 0 && (
            <span style={{ fontSize: 10, color: "var(--kpa-slate-300)", fontStyle: "italic" }}>no entries</span>
          )}
          {!disabled && canCreate && (
            <button
              type="button"
              className="kpa-row-add-btn"
              onClick={e => { e.stopPropagation(); setOpen(true); setShowAddPanel(true); }}
            >
              <i className="fas fa-plus" style={{ fontSize: 9 }} aria-hidden="true" /> Add
            </button>
          )}
        </div>
      </div>

      {open && (
        <div style={{ background: "var(--kpa-slate-50)", borderTop: "1px solid var(--kpa-slate-100)" }}>
          {showAddPanel && !disabled && canCreate && (
            <AddAssetPanel
              candidates={[{ kpiId: kpi.questionId, kpiTitle: kpi.title, assets }]}
              selectedFYId={selectedFYId}
              sources={sources}
              onSaved={onSaved}
              onClose={() => setShowAddPanel(false)}
            />
          )}

          {assets.length === 0 && !showAddPanel ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.3 }} aria-hidden="true">🏭</div>
              <div style={{ fontSize: 12, color: "var(--kpa-slate-400)", marginBottom: 12 }}>No assets for this KPI yet.</div>
              {!disabled && canCreate && (
                <button type="button" className="kpa-btn kpa-btn--primary" style={{ margin: "0 auto" }} onClick={() => setShowAddPanel(true)}>
                  <i className="fas fa-plus" style={{ marginRight: 6 }} aria-hidden="true" />Add first asset / global asset
                </button>
              )}
            </div>
          ) : assets.length > 0 && (
            <div className="kpa-inner-list">
              <div className="kpa-inner-list-header">
                <span className="kpa-list-header-label">
                  {assets.some(a => a.isLocationRequired) ? "Assets" : "Global Assets"}
                </span>
                <span style={{ fontSize: 10, color: "var(--kpa-slate-400)" }}>
                  {assets.length} item{assets.length !== 1 ? "s" : ""}
                </span>
              </div>
              {assets.map(asset => {
                if (String(editingAssetId) === String(asset.id)) {
                  return (
                    <AssetEditRow
                      key={asset.id}
                      asset={asset}
                      sources={sources}
                      allAssets={assets}
                      onSave={(newTitle) => handleEditSave(asset.id, newTitle)}
                      onCancel={() => setEditingAssetId(null)}
                    />
                  );
                }
                return (
                  <AssetReadRow
                    key={asset.id}
                    asset={asset}
                    sources={sources}
                    onEdit={a => setEditingAssetId(a.id)}
                    onDelete={a => handleDeleteSingle(a.id)}
                    disabled={disabled || deletingId === asset.id}
                    deleting={deletingId === asset.id}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Memoized: page-level search/filter keystrokes re-render the list, but rows
 * with unchanged props (primitives + stable callbacks) skip their subtree.
 */
export default memo(KpiAssetRow);
